import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
} from '@fitogest/reactive-form-builder/interfaces';
import { Filtering } from '../../../../../../service/filtering/filtering';
import { ProductosFitosanitariosEnvironmentDefinition } from '../../environment-variables';
import { ProductosFitosanitariosListsDefinition } from './list-variables';
import { ColInterface } from '../../../../../../common/components/common-list-table/col.interface';
import { ProductosModel } from '../../../../../../models/productos/productos.model';
import { StorageManager } from '../../../../../../common/storage-manager.class';

export class ProductosFitosanitariosFormDefinition {
    public formType!: FormType;

    public env: ProductosFitosanitariosEnvironmentDefinition = new ProductosFitosanitariosEnvironmentDefinition();

    public lists: ProductosFitosanitariosListsDefinition = new ProductosFitosanitariosListsDefinition();

    public model: ProductosModel = {} as ProductosModel;

    public openDialog = false;

    public usosFilter: Filtering<ProductosModel> = new Filtering<ProductosModel>();

    public agentesFilter: Filtering<ProductosModel> = new Filtering<ProductosModel>();

    public cols: ColInterface[] = [
        { field: 'referencia', header: 'N˚ Registro', filter: '' },
        { field: 'nombre_comercial', header: 'Nombre', filter: '' },
        { field: 'titular', header: 'Titular', filter: '' },
        { field: 'fecha_inscripcion', header: 'Inscripción', filter: '' },
        { field: 'fecha_caducidad', header: 'Caducidad', filter: '' },
        { field: 'composicion', header: 'Composición', filter: '' }
    ];


    public formFieldsSection!: FormSection<object>;

    public formFieldsPrevilsa1!: FormSection<object>;

    public formFieldsPrevilsa2!: FormSection<object>;

    public formFieldsEstadosReglas!: FormSection<object>;

    public formFields!: Form<ProductosModel>;

    public hiddenFormFields!: Form<any>;

    public fieldNames: FormField<any>[] = [];

    public fieldLabels: string[] = [];


    constructor(
        type: FormType,
        _model: Record<string, string>,
        table: null
    ) {
        this.formType = +type;
        this.model = _model as unknown as ProductosModel;

        this.formFields = {
            name: 'productos',
            header: 'Datos del Producto',
            type: this.formType,
            sections: this.getFormSections(),
            table: table,
            uploadButton: {
                isEnabled: this.env.appName === 'agroleza',
                label: 'Subir Foto'
            },
            signed: false
        };
    }


    public getTopFormFields(): FormField<any>[] {
        const topFormFields: FormField<any>[] = [
            {
                name: 'otra_denominacion_comun',
                label: 'Otra denominación común',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'num_registro',
                label: 'Nº Registro',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'num_registro',
                        swap: (it: FormField<object>): FormField<object> => {
                            const useOtherCommonDenomination = this.isOtherCommonDenominationEnabled();
                            const self = it;

                            if (self.input.dropdownConfiguration) {
                                self.input.dropdownConfiguration.valuePrimaryKey = useOtherCommonDenomination
                                    ? 'num_registro'
                                    : 'referencia';
                            }

                            self.input.data = useOtherCommonDenomination
                                ? this.lists.denominaciones_otras
                                : this.lists.denominaciones_estandar;

                            return self;
                        },
                    }
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                buttons: [{
                    class: 'btn btn-light border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
                    style: `
                        width: 24px;
                        height: 24px;
                        margin-left: 6px;
                        font-size: 0.9rem;
                        transform: translateY(-1rem);
                    `,
                    icon: 'fa fa-info text-primary',
                    label: '',
                    tooltip: 'Más información sobre el producto',
                    tooltipPosition: 'top',
                    function: () => this.openDialog = true
                }],
                order: 2
            },
            {
                name: 'nombre',
                label: 'Nombre del producto',
                input: { type: InputType.TEXT, isEditable: true },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'imagen',
                label: '',
                input: { type: InputType.EMPTY, isEditable: false },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agroleza'
                },
                order: 4
            }
        ];

        this.fieldNames = this.fieldNames.concat(topFormFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(topFormFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return topFormFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }


    public getBottomFormFieldsLeft(): FormField<any>[] {
        const bottomFormFieldsLeft: FormField<any>[] = [
            {
                name: 'precio',
                label: this.env.appName === 'agrotata' ? 'Precio Coste' : 'Precio',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'precio_venta',
                label: 'Precio de Venta',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agrotata'
                },
                order: 1
            },
            {
                name: 'familia',
                label: 'Familia',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.familias,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agroleza'
                },
                order: 2
            },
            {
                name: 'unidades',
                label: 'Uds. Medida',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.unidades_costes,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'envase',
                label: 'Envase',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: 'dosis',
                label: 'Dosis',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showProductosFitoDosis
                },
                order: 5
            },
            {
                name: 'num_maximo_aplicaciones',
                label: 'Nº Máximo aplicaciones',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showProductosFitoDosis
                },
                order: 6
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXT,
                    isEditable: true,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'amoros'
                },
                order: 7
            },
            {
                name: 'ultima_fecha_compra',
                label: 'Última fecha compra',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'amoros'
                },
                order: 8
            },
              {
                label: 'Materia Activa',
                name: 'materia_activa',
                input: {
                    type: InputType.ONLY_TEXT,
                    isEditable: false,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            }
        ];

        this.fieldNames = this.fieldNames.concat(bottomFormFieldsLeft.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(bottomFormFieldsLeft.filter((it) => it.conditions.isVisible).map(it => it.label));
        return bottomFormFieldsLeft.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getHiddenFields(): FormField<any>[] {
        const hiddenFields: FormField<any>[] = [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    isEditable: false,
                    defaultValue: StorageManager.getUser().id,
                    type: InputType.HIDDEN,
                },
                updateValue: () => this.model.id_usuario,
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    isEditable: false,
                    defaultValue: StorageManager.getClient()?.id,
                    type: InputType.HIDDEN,
                },
                updateValue: () => this.model.id_cliente,
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 3
            },
            {
                name: 'tipo',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: 'fitosanitario',
                    isEditable: false,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 6
            },
        ];

        this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(hiddenFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getFormSections(): FormSection<any>[] {
        const sections: FormSection<any>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getTopFormFields(),
                order: 1
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getBottomFormFieldsLeft(),
                order: 2
            },
            // TODO: Hidden fields must be always at the end of the form definition
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3
            }
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private isOtherCommonDenominationEnabled(): boolean {
        const value = this.model.otra_denominacion_comun as unknown;

        return value === '1' || value === 1 || value === true;
    }
}